import { useEffect, useState } from 'react';
import { useApi } from '../services/api';
import { Link } from 'react-router-dom';
import { GameStatus } from '@bst-stellar/shared';

interface UserStats {
    rank: number | null;
    completedSquares: number;
    bingoLines: number;
    score: number;
}

interface UserGameStatsProps {
    gameId: string;
    gameStatus?: string;
    revealSchedule?: {
        enabled?: boolean;
        time?: string;
        durationHours?: number;
    };
}

export default function UserGameStats({ gameId, gameStatus, revealSchedule }: UserGameStatsProps) {
    const api = useApi();
    const [stats, setStats] = useState<UserStats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadUserStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameId]);

    async function loadUserStats() {
        try {
            setLoading(true);
            // Use ownOnly=true to get user's own stats without reveal time restrictions
            const data = await api.getGameLeaderboard(gameId, true);

            // Check if we have currentUserStats from the API
            if (data.currentUserStats) {
                setStats({
                    rank: data.currentUserRank || null,
                    completedSquares: data.currentUserStats.completedSquares,
                    bingoLines: data.currentUserStats.bingoLines,
                    score: data.currentUserStats.score,
                });
            } else if (data.currentUserRank) {
                // Fallback if only rank is available
                setStats({
                    rank: data.currentUserRank,
                    completedSquares: 0,
                    bingoLines: 0,
                    score: 0,
                });
            } else {
                setStats(null);
            }
        } catch (error: any) {
            console.error('Failed to load user stats:', error);
            setStats(null);
        } finally {
            setLoading(false);
        }
    }

    const isGameCompleted = gameStatus === GameStatus.COMPLETED;
    const showRevealLink = revealSchedule?.enabled && !isGameCompleted;

    if (loading) {
        return (
            <div className="bg-surface rounded-lg shadow p-6 border border-border">
                <h3 className="text-lg font-bold mb-4 text-text-primary">📊 Your Progress</h3>
                <div className="text-center py-4 text-text-muted">
                    Loading your stats...
                </div>
            </div>
        );
    }

    return (
        <div className="bg-surface rounded-lg shadow p-6 border border-border">
            <h3 className="text-lg font-bold mb-4 text-text-primary">📊 Your Progress</h3>

            {stats ? (
                <div className="space-y-4">
                    {/* Zero-state: no activity yet */}
                    {stats.score === 0 && stats.completedSquares === 0 && stats.bingoLines === 0 ? (
                        <div className="text-center py-4 text-text-muted">
                            🚀 Get started by submitting your first claim to see your progress!
                        </div>
                    ) : (
                        <>
                            {/* Score display */}
                            <div className="text-center">
                                <div className="text-4xl font-bold text-primary mb-1">
                                    {stats.score}
                                </div>
                                <div className="text-sm text-text-muted">points</div>
                            </div>

                            {/* Stats grid */}
                            <div className="grid grid-cols-2 gap-3 text-center">
                                <div className="bg-primary/10 rounded-lg p-3">
                                    <div className="text-xl font-bold text-text-primary">
                                        {stats.completedSquares}
                                    </div>
                                    <div className="text-xs text-text-muted">squares</div>
                                </div>
                                <div className="bg-green-500/10 rounded-lg p-3">
                                    <div className="text-xl font-bold text-text-primary">
                                        {stats.bingoLines}
                                    </div>
                                    <div className="text-xs text-text-muted">bingos</div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Reveal link */}
                    {showRevealLink && (
                        <div className="pt-2">
                            <Link
                                to={`/games/${gameId}/reveal`}
                                className="block text-center text-sm text-primary hover:underline"
                            >
                                👀 View full leaderboard during reveal →
                            </Link>
                        </div>
                    )}

                    {/* Link to reveal for completed games */}
                    {isGameCompleted && (
                        <div className="pt-2">
                            <Link
                                to={`/games/${gameId}/reveal`}
                                className="block text-center text-sm bg-primary text-white rounded-lg py-2 hover:bg-primary/90"
                            >
                                🏆 View Final Results & Winners
                            </Link>
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-center py-4 text-text-muted">
                    Complete squares to see your progress!
                </div>
            )}
        </div>
    );
}
